import { z } from "zod";
import { LOCATION_TYPES } from "../enums.ts";
import { Address } from "./common.ts";

/* ------------------------------- imports ------------------------------- */
export const IMPORT_MODULES = ["agencies", "companies", "contacts", "master_ingredients", "ingredient_request", "schedule_b", "facilities", "plants", "company_plant_links", "listings", "certificates", "qbo_customers", "company_ingredients", "products", "formulas", "productions", "issues", "fees", "inspections", "schedule_windows", "caterers", "events", "complaints", "directory_entries"] as const;
export type ImportModule = (typeof IMPORT_MODULES)[number];
/** Where the rows came from; recorded on the job and stamped on every row it creates as `legacy_source`. */
export const IMPORT_SOURCES = ["csv", "portal", "clickup", "kosherseal", "qbo"] as const;
export type ImportSource = (typeof IMPORT_SOURCES)[number];
/** apply: the source is the master and overwrites · delta: brings only what changed in the source and never overwrites a platform edit. */
export const IMPORT_RUN_MODES = ["apply", "delta"] as const;
export type ImportRunMode = (typeof IMPORT_RUN_MODES)[number];

export const ImportDryRunInput = z.object({
  module: z.enum(IMPORT_MODULES),
  mapping: z.record(z.string(), z.string()),
  companyId: z.string().uuid().nullable().optional(),
  mappingName: z.string().max(100).nullable().optional(),
  mode: z.enum(IMPORT_RUN_MODES).default("apply"),
  /** the slice of rows to process in this call (0-based over data rows); the client repeats until done. Each row costs a few sequential queries, so a slice is capped at 500 — a 500-row slice took minutes against Neon through Hyperdrive and the proxy answered 503 (staging, 2026-10-29). */
  from: z.number().int().min(0).default(0),
  batch: z.number().int().min(1).max(500).default(100),
});

export const ImportApplyInput = z.object({
  companyId: z.string().uuid().nullable().optional(),
  mode: z.enum(IMPORT_RUN_MODES).optional(),
  from: z.number().int().min(0).default(0),
  batch: z.number().int().min(1).max(500).default(100),
});

export const ImportMappingInput = z.object({
  module: z.enum(IMPORT_MODULES),
  name: z.string().min(1).max(100),
  mapping: z.record(z.string(), z.string()),
  companyId: z.string().uuid().nullable().optional(),
});

/* ------------------------------- listings ------------------------------ */
export const LISTING_KINDS = ["main", "additional_brand", "private_label"] as const;

export const ListingInput = z.object({
  companyId: z.string().uuid(),
  listingKind: z.enum(LISTING_KINDS).default("main"),
  listedName: z.string().min(1).max(200),
  brand: z.string().max(200).nullable().optional(),
  dba: z.string().max(200).nullable().optional(),
  tradeName: z.string().max(200).nullable().optional(),
  publicAddress: Address.nullable().optional(),
  publicPhone: z.string().max(60).nullable().optional(),
  publicEmail: z.string().email().max(200).nullable().optional(),
  publicWebsite: z.string().max(300).nullable().optional(),
  storefront: z.boolean().default(false),
  seasonal: z.boolean().default(false),
  seasonalCategory: z.string().max(80).nullable().optional(),
  listingCategory: z.string().max(80).nullable().optional(),
  categories: z.array(z.string().max(80)).default([]),
  notes: z.string().max(4000).nullable().optional(),
  kosher: z
    .object({
      certificationStatus: z.string().max(60).nullable().optional(),
      restrictionType: z.string().max(60).nullable().optional(),
      kosherGroup: z.string().max(60).nullable().optional(),
      kosherForPesach: z.boolean().nullable().optional(),
      cholovYisroel: z.boolean().nullable().optional(),
      pasYisroel: z.boolean().nullable().optional(),
      mevushal: z.boolean().nullable().optional(),
      fish: z.boolean().nullable().optional(),
      kitniyos: z.boolean().nullable().optional(),
      wordingPublic: z.array(z.string()).default([]),
      wordingNotPublic: z.array(z.string()).default([]),
      wordingTzitzis: z.array(z.string()).default([]),
      wordingTashmishei: z.array(z.string()).default([]),
    })
    .optional(),
});
export type ListingInput = z.infer<typeof ListingInput>;
export const ListingUpdateInput = ListingInput.partial().omit({ companyId: true });

/* -------------------------- location authorizations -------------------- */
export const AuthorizationInput = z.object({
  companyId: z.string().uuid(),
  productId: z.string().uuid().nullable().optional(),
  productName: z.string().max(300).nullable().optional(),
  locationType: z.enum(LOCATION_TYPES),
  locationId: z.string().uuid().nullable().optional(),
  newLocationName: z.string().max(200).nullable().optional(),
  notes: z.string().max(4000).nullable().optional(),
});
export type AuthorizationInput = z.infer<typeof AuthorizationInput>;

export const AuthorizationUpdateInput = z.object({
  rcPersonId: z.string().uuid().nullable().optional(),
  mashgiachPersonId: z.string().uuid().nullable().optional(),
  inspectionId: z.string().uuid().nullable().optional(),
  inspectionDate: z.string().max(20).nullable().optional(),
  companyProtocolDocumentId: z.string().uuid().nullable().optional(),
  mashgiachProtocolDocumentId: z.string().uuid().nullable().optional(),
  ingredientSubmissionIds: z.array(z.string().uuid()).optional(),
  kosherProfile: z.record(z.unknown()).optional(),
  notes: z.string().max(4000).nullable().optional(),
  locationId: z.string().uuid().nullable().optional(),
});

/* ------------------------------- compliance ---------------------------- */
export const ISSUE_SOURCES = ["inspection", "complaint", "reconciliation", "document_review"] as const;

export const IssueInput = z.object({
  companyId: z.string().uuid().nullable().optional(),
  facilityId: z.string().uuid().nullable().optional(),
  plantId: z.string().uuid().nullable().optional(),
  productId: z.string().uuid().nullable().optional(),
  title: z.string().min(1).max(300),
  description: z.string().max(8000).nullable().optional(),
  severity: z.string().max(40).default("minor"),
  ownerPersonId: z.string().uuid().nullable().optional(),
  team: z.string().max(40).nullable().optional(),
});

export const IssueUpdateInput = z.object({
  title: z.string().min(1).max(300).optional(),
  description: z.string().max(8000).nullable().optional(),
  severity: z.string().max(40).optional(),
  ownerPersonId: z.string().uuid().nullable().optional(),
  team: z.string().max(40).nullable().optional(),
  awaitingCompany: z.boolean().optional(),
  awaitingCaller: z.boolean().optional(),
});

export const IssueTransitionInput = z.object({
  status: z.string().max(40),
  resolutionNote: z.string().max(4000).nullable().optional(),
});

/** Staff phone intake. */
export const ComplaintInput = z.object({
  companyId: z.string().uuid().nullable().optional(),
  facilityId: z.string().uuid().nullable().optional(),
  plantId: z.string().uuid().nullable().optional(),
  productId: z.string().uuid().nullable().optional(),
  complaintType: z.string().max(60),
  title: z.string().min(1).max(300),
  description: z.string().max(8000).nullable().optional(),
  severity: z.string().max(40).default("minor"),
  complainantName: z.string().max(200).nullable().optional(),
  complainantPhone: z.string().max(60).nullable().optional(),
  complainantEmail: z.string().email().max(200).nullable().optional(),
  alreadyInContact: z.boolean().default(false),
  occurredAt: z.string().max(30).nullable().optional(),
  ownerPersonId: z.string().uuid().nullable().optional(),
  team: z.string().max(40).nullable().optional(),
});

/** Public web form: anonymous allowed; the company is picked from published listings only. */
export const PublicComplaintInput = z.object({
  listingId: z.string().uuid().nullable().optional(),
  businessNameText: z.string().max(200).nullable().optional(),
  complaintType: z.string().max(60).default("other"),
  description: z.string().min(10).max(8000),
  occurredAt: z.string().max(30).nullable().optional(),
  complainantName: z.string().max(200).nullable().optional(),
  complainantPhone: z.string().max(60).nullable().optional(),
  complainantEmail: z.string().email().max(200).nullable().optional(),
  anonymous: z.boolean().default(false),
  fax: z.string().max(200).optional(),
}).strict();

export const DisclosureInput = z.object({
  message: z.string().min(1).max(8000),
  toEmails: z.array(z.string().email()).min(1),
});

export const EscalationRuleInput = z.object({
  severity: z.string().max(40),
  openForDays: z.number().int().min(1).max(365),
  notifyPersonId: z.string().uuid().nullable().optional(),
  notifyRoleKey: z.string().max(40).nullable().optional(),
  active: z.boolean().default(true),
});

/* ------------------------------ access tokens -------------------------- */
export const IssueLinkInput = z.object({
  personId: z.string().uuid(),
  /** days after the run ends that the link stays valid */
  marginDays: z.number().int().min(0).max(60).default(3),
});
